import { Box, Button, HStack, Text, VStack } from 'components/ui';
import { useEffect, useRef, useState } from 'react';
import { MdDriveFileMove, MdFolder, MdFolderOpen } from 'react-icons/md';

interface FolderOption {
  id: string;
  name: string;
  parentName?: string;
}

interface MoveFolderPopoverProps {
  folders: FolderOption[];
  currentFolderId?: string | null;
  onMove: (folderId: string | null) => Promise<void>;
}

const MoveFolderPopover = ({ folders, currentFolderId, onMove }: MoveFolderPopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const handleMove = async (folderId: string | null) => {
    setBusy(true);
    try {
      await onMove(folderId);
      setIsOpen(false);
      setSearch('');
    } finally {
      setBusy(false);
    }
  };

  const filtered = folders.filter(f =>
    f.name.toLowerCase().includes(search.toLowerCase()) ||
    (f.parentName && f.parentName.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <Box position="relative" ref={containerRef}>
      <Button
        size="xs"
        variant="ghost"
        title="Move to folder"
        onClick={() => setIsOpen(v => !v)}
        p={1}
      >
        <MdDriveFileMove size={16} />
      </Button>

      {isOpen && (
        <Box
          position="absolute"
          right={0}
          top="100%"
          mt={1}
          zIndex={200}
          bg={{ base: 'white', _dark: 'gray.800' }}
          border="1px solid"
          borderColor={{ base: 'gray.200', _dark: 'gray.600' }}
          borderRadius="lg"
          boxShadow="lg"
          minW="220px"
          maxH="280px"
          display="flex"
          flexDirection="column"
        >
          {/* Search */}
          <Box px={2} pt={2} pb={1} borderBottom="1px solid" borderColor={{ base: 'gray.100', _dark: 'gray.700' }}>
            <input
              autoFocus
              placeholder="Search folders…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%',
                fontSize: '12px',
                padding: '4px 8px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                outline: 'none',
                background: 'transparent',
              }}
            />
          </Box>

          <VStack align="stretch" gap={0} overflowY="auto" flex={1}>
            {/* Root option */}
            {currentFolderId !== null && (
              <HStack
                gap={2}
                px={3}
                py={2}
                cursor="pointer"
                _hover={{ bg: { base: 'gray.50', _dark: 'gray.700' } }}
                onClick={() => !busy && handleMove(null)}
                opacity={busy ? 0.5 : 1}
              >
                <Box color={{ base: 'gray.400', _dark: 'gray.500' }}>
                  <MdFolderOpen size={15} />
                </Box>
                <Text fontSize="sm" color={{ base: 'gray.700', _dark: 'gray.200' }}>Root (uncategorized)</Text>
              </HStack>
            )}

            {/* Folder options */}
            {filtered.map(folder => (
              <HStack
                key={folder.id}
                gap={2}
                px={3}
                py={2}
                cursor={folder.id === currentFolderId ? 'default' : 'pointer'}
                _hover={folder.id !== currentFolderId ? { bg: { base: 'gray.50', _dark: 'gray.700' } } : {}}
                onClick={() => !busy && folder.id !== currentFolderId && handleMove(folder.id)}
                opacity={folder.id === currentFolderId || busy ? 0.5 : 1}
              >
                <Box color={{ base: 'yellow.500', _dark: 'yellow.400' }} flexShrink={0}
                  pl={folder.parentName ? '12px' : 0}>
                  <MdFolder size={15} />
                </Box>
                <VStack align="start" gap={0} flex={1}>
                  <Text fontSize="sm" color={{ base: 'gray.700', _dark: 'gray.200' }}>{folder.name}</Text>
                  {folder.parentName && (
                    <Text fontSize="xs" color={{ base: 'gray.400', _dark: 'gray.500' }}>{folder.parentName}</Text>
                  )}
                </VStack>
                {folder.id === currentFolderId && (
                  <Text fontSize="xs" color={{ base: 'gray.400', _dark: 'gray.500' }}>current</Text>
                )}
              </HStack>
            ))}

            {filtered.length === 0 && (
              <Text fontSize="sm" color={{ base: 'gray.400', _dark: 'gray.500' }} px={3} py={2}>
                No folders found
              </Text>
            )}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

export default MoveFolderPopover;
